import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemBanco } from '../mensagemBanco';
import ModalConfirmarAcao from './ModalConfirmarAcao';

interface SituacaoQR {
  ok: boolean;
  aberto?: boolean;
  motivo?: string | null;
  fecha_em?: string | null;
}

interface Props {
  encontroId: number;
}

function formatarHora(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return min === '00' ? `${hh}h` : `${hh}h${min}`;
}

export default function CaixaQRChamada({ encontroId }: Props) {
  const [situacao, setSituacao] = useState<SituacaoQR | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [confirmando, setConfirmando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erroAcao, setErroAcao] = useState<string | null>(null);

  async function carregar() {
    const { data, error } = await supabase.rpc('situacao_do_qr', {
      p_encontro: encontroId,
    });
    if (error) throw error;
    setSituacao((data as SituacaoQR) ?? null);
  }

  useEffect(() => {
    let ativo = true;
    (async () => {
      setCarregando(true);
      try {
        await carregar();
        if (ativo) setCarregando(false);
      } catch {
        if (ativo) {
          setSituacao(null);
          setCarregando(false);
        }
      }
    })();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encontroId]);

  async function mudarChamada(fechar: boolean) {
    setSalvando(true);
    setErroAcao(null);
    try {
      const { data, error } = await supabase.rpc('fechar_presenca', {
        p_encontro: encontroId,
        p_fechar: fechar,
      });
      if (error) throw error;
      const resp = data as { ok: boolean; erro?: string } | null;
      if (!resp || resp.ok === false) {
        setErroAcao(resp?.erro || 'Não foi possível. Tente de novo.');
        return;
      }
      setConfirmando(false);
      await carregar();
    } catch (e) {
      setErroAcao(mensagemBanco(e));
    } finally {
      setSalvando(false);
    }
  }

  if (carregando) {
    return <div className="h-24 animate-pulse rounded-2xl bg-secondary-100" />;
  }

  if (!situacao || situacao.ok === false) return null;

  const aberto = situacao.aberto === true;

  return (
    <div className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
            aberto ? 'bg-green-50 text-green-700' : 'bg-secondary-100 text-secondary-600'
          }`}
        >
          <i className={`${aberto ? 'ri-lock-unlock-line' : 'ri-lock-line'} text-xl`} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-bold text-foreground-950">
            {aberto ? 'Chamada aberta' : 'Chamada fechada'}
          </p>
          <p className="mt-0.5 text-sm text-secondary-500">
            {aberto
              ? `Fecha sozinha às ${formatarHora(situacao.fecha_em)}. Você pode fechar antes.`
              : situacao.motivo || ''}
          </p>
        </div>
      </div>

      {erroAcao && <p className="mt-3 text-sm text-red-600">{erroAcao}</p>}

      <button
        type="button"
        onClick={() => (aberto ? setConfirmando(true) : mudarChamada(false))}
        disabled={salvando}
        className="mt-4 flex h-11 min-h-[44px] w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 text-sm font-bold text-foreground-800 transition-colors hover:bg-background-100 disabled:opacity-60"
      >
        {aberto ? 'Encerrar a chamada agora' : 'Reabrir a chamada'}
      </button>

      {confirmando && (
        <ModalConfirmarAcao
          titulo="Encerrar a chamada"
          mensagem="Ninguém mais vai conseguir marcar presença com o QR. As fotos que já chegaram continuam para você aprovar."
          textoConfirmar="Encerrar"
          salvando={salvando}
          aoConfirmar={() => mudarChamada(true)}
          aoCancelar={() => setConfirmando(false)}
        />
      )}
    </div>
  );
}